import map from 'lodash/map';
import * as React from 'react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

export type ChipLegendItem = {
  key: string;
  label: string;
  color: string;
  hidden?: boolean;
  swatch?: 'dot' | 'dash';
  tooltip?: string;
  emphasized?: boolean;
};

type Props = {
  items: ChipLegendItem[];
  onToggle: (key: string) => void;
  className?: string;
};

// Inline chip styling mirrors the `outline` Badge variant. Rendered as a
// forwardRef'd <button> rather than the shared <Badge> so that Radix's
// TooltipTrigger asChild can attach its ref directly — Badge is a plain
// function component without forwardRef, which silently breaks tooltip
// positioning (Floating UI can't find the anchor and pins it to 0,0).
type ChipProps = {
  item: ChipLegendItem;
  onToggle: (key: string) => void;
};

const Chip = React.forwardRef<HTMLButtonElement, ChipProps & React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ item, onToggle, ...rest }, ref) => (
    <button
      ref={ref}
      type="button"
      {...rest}
      // Compose with rest.onClick: when TooltipTrigger asChild wraps this
      // button, Radix passes its own onClick via Slot which would otherwise
      // override ours and break the toggle.
      onClick={(e) => { rest.onClick?.(e); onToggle(item.key); }}
      aria-pressed={!item.hidden}
      aria-label={item.hidden ? `Show ${item.label}` : `Hide ${item.label}`}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs whitespace-nowrap cursor-pointer hover:bg-accent focus:outline-hidden focus-visible:ring-1 focus-visible:ring-ring ${item.hidden ? 'opacity-40' : ''}`}
      style={{ borderColor: item.color }}
    >
      {item.swatch === 'dash' ? (
        <span className="inline-block w-3 h-0.5" style={{ borderTop: `2px dashed ${item.color}` }} />
      ) : (
        <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
      )}
      <span className={`text-[11px] ${item.emphasized ? 'font-semibold' : ''}`}>{item.label}</span>
    </button>
  ),
);
Chip.displayName = 'Chip';

export function ChipLegend ({ items, onToggle, className }: Props) {
  return (
    <TooltipProvider>
      <div className={`flex flex-wrap items-center gap-1.5 ${className ?? ''}`}>
        {map(items, (item) => {
          if (!item.tooltip) {
            return <Chip key={item.key} item={item} onToggle={onToggle} />;
          }
          return (
            <Tooltip key={item.key}>
              <TooltipTrigger asChild>
                <Chip item={item} onToggle={onToggle} />
              </TooltipTrigger>
              <TooltipContent>{item.tooltip}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
